import { useBreakpointValue } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

const MAX_ROW_LENGTH = 6;

const DEFAULT_ROW_DISTRIBUTION = {
  base: 2,
  md: 3,
  lg: MAX_ROW_LENGTH,
};

export const useDistributedRows = <T>(
  items: T[],
  rowDistribution?: Record<string, number>,
) => {
  const [rows, setRows] = useState<Array<Array<T>>>([]);
  /* INFO: rowLength changes based on breakpoint */
  const rowLength = useBreakpointValue(
    rowDistribution || DEFAULT_ROW_DISTRIBUTION,
    { fallback: 'lg' },
  ) as number;

  useEffect(() => {
    const calculateRowLengths = (totalItems: number, maxRowLength: number) => {
      if (totalItems <= maxRowLength) {
        return [totalItems];
      }

      const hasOddItemsWithMaxRowOfTwo =
        maxRowLength === 2 && totalItems % 2 === 1;

      if (hasOddItemsWithMaxRowOfTwo) {
        const remainingItems = totalItems - 1;
        const pairsNeeded = remainingItems / 2;
        const rowLengths = [1, ...Array<number>(pairsNeeded).fill(2)];
        return rowLengths;
      }

      const minimumRowCount = Math.ceil(totalItems / maxRowLength);
      const baseItemsPerRow = Math.floor(totalItems / minimumRowCount);
      const extraItems = totalItems % minimumRowCount;

      const rowLengths = [];

      /* eslint-disable-next-line no-plusplus */
      for (let rowIndex = 0; rowIndex < minimumRowCount; rowIndex++) {
        const shouldReceiveExtraItem = rowIndex < extraItems;
        const itemsInThisRow =
          baseItemsPerRow + (shouldReceiveExtraItem ? 1 : 0);
        rowLengths.push(itemsInThisRow);
      }

      return rowLengths.sort((first, second) => second - first);
    };

    const distributeItems = <U>(allItems: U[], maxRowLength: number) => {
      const rowLengths = calculateRowLengths(allItems.length, maxRowLength);
      const distributedItems = [];

      let currentItemIndex = 0;

      for (const rowLen of rowLengths) {
        const rowItems = allItems.slice(
          currentItemIndex,
          currentItemIndex + rowLen,
        );
        distributedItems.push(rowItems);
        currentItemIndex += rowLen;
      }

      return distributedItems;
    };

    const chunks = distributeItems(items, rowLength);
    setRows(chunks);
  }, [rowLength, items]);

  return rows;
};
